/**
 * Deepwake ship classes — all original. Movement params feed the Destiny
 * integrator directly (mass/agility/maxVel exactly as EVE used them).
 * warpFactor is milli-AU/s. Combat/fitting stats are consumed by the
 * game layer (Phase 4 expands modules & fitting).
 */

export interface ShipClassDef {
  key: string;
  name: string;
  role: string;
  description: string;
  radius: number;
  mass: number;
  agility: number;
  maxVel: number;
  warpFactor: number;
  hull: number;
  armor: number;
  shield: number;
  capacitor: number;
  capRegen: number; // cap/s
  cargo: number; // m3
  slots: { weapon: number; system: number; rig: number };
  price: number; // Marks, foundry base cost
}

export const SHIP_CLASSES: Record<string, ShipClassDef> = {
  skiff: {
    key: 'skiff',
    name: 'Skiff',
    role: 'Miner',
    description:
      'A Provenance pattern older than the Stilling: a drum of habitat wrapped around an ore maw. Slow, stubborn, and the backbone of every foundry ledger in the Reach.',
    radius: 18,
    mass: 11_000_000,
    agility: 1.1,
    maxVel: 160,
    warpFactor: 2500,
    hull: 700,
    armor: 500,
    shield: 600,
    capacitor: 320,
    capRegen: 3.2,
    cargo: 480,
    slots: { weapon: 1, system: 2, rig: 1 },
    price: 9_000,
  },
  dart: {
    key: 'dart',
    name: 'Dart',
    role: 'Frigate',
    description:
      'The Tidemark answer to a dangerous sky: three engines, one gun deck, and nothing you will miss when it burns. Wakers learn to fly in Darts because Darts forgive nothing.',
    radius: 12,
    mass: 1_100_000,
    agility: 3.1,
    maxVel: 420,
    warpFactor: 5000,
    hull: 350,
    armor: 300,
    shield: 400,
    capacitor: 260,
    capRegen: 3.4,
    cargo: 120,
    slots: { weapon: 3, system: 2, rig: 1 },
    price: 6_000,
  },
  mule: {
    key: 'mule',
    name: 'Mule',
    role: 'Hauler',
    description:
      'Boxes bolted to a reactor. The Compact swears the design predates Earth-myth; nobody has improved it because nothing this ugly can be improved, only repainted.',
    radius: 30,
    mass: 16_000_000,
    agility: 0.9,
    maxVel: 140,
    warpFactor: 3000,
    hull: 900,
    armor: 450,
    shield: 500,
    capacitor: 380,
    capRegen: 3.0,
    cargo: 3_600,
    slots: { weapon: 0, system: 3, rig: 1 },
    price: 14_000,
  },
  lance: {
    key: 'lance',
    name: 'Lance',
    role: 'Destroyer',
    description:
      'A Vigil escort hull: a single long spinal rail with a cockpit as an afterthought. Built to kill the Unbound at range and tithe the wrecks to the gate shrines.',
    radius: 22,
    mass: 2_600_000,
    agility: 2.4,
    maxVel: 310,
    warpFactor: 4500,
    hull: 600,
    armor: 700,
    shield: 550,
    capacitor: 420,
    capRegen: 3.9,
    cargo: 200,
    slots: { weapon: 5, system: 2, rig: 1 },
    price: 22_000,
  },
  reliquary: {
    key: 'reliquary',
    name: 'Reliquary',
    role: 'Cruiser',
    description:
      "Warden's-pattern: grown around a salvaged gate fragment nobody can cut open. The fragment hums in warp. Crews report dreams. The Vigil pays double for logged recordings.",
    radius: 42,
    mass: 9_800_000,
    agility: 1.6,
    maxVel: 240,
    warpFactor: 4000,
    hull: 1_400,
    armor: 1_500,
    shield: 1_600,
    capacitor: 900,
    capRegen: 6.5,
    cargo: 420,
    slots: { weapon: 5, system: 4, rig: 2 },
    price: 86_000,
  },
  bastion: {
    key: 'bastion',
    name: 'Bastion',
    role: 'Industrial',
    description:
      'A mobile foundry line the Combine licenses but never sells. Refines at sites the Stilling stranded past any station — if its escort holds.',
    radius: 60,
    mass: 38_000_000,
    agility: 0.65,
    maxVel: 100,
    warpFactor: 2000,
    hull: 2_600,
    armor: 1_800,
    shield: 2_000,
    capacitor: 1_400,
    capRegen: 7.2,
    cargo: 8_000,
    slots: { weapon: 2, system: 5, rig: 2 },
    price: 210_000,
  },
};

export const STARTER_SHIP = 'dart';

export interface OreDef {
  key: string;
  name: string;
  volume: number; // m3 per unit
  refineTo: Record<string, number>; // mineral -> units per batch of 100
  basePrice: number;
}

export const ORES: Record<string, OreDef> = {
  cindral: {
    key: 'cindral',
    name: 'Cindral',
    volume: 0.1,
    refineTo: { ferrite: 70, lumen: 10 },
    basePrice: 6,
  },
  veilspar: {
    key: 'veilspar',
    name: 'Veilspar',
    volume: 0.15,
    refineTo: { ferrite: 40, lumen: 40 },
    basePrice: 11,
  },
  tidegold: {
    key: 'tidegold',
    name: 'Tidegold',
    volume: 0.25,
    refineTo: { lumen: 55, wakemetal: 25 },
    basePrice: 26,
  },
  stillore: {
    key: 'stillore',
    name: 'Stillore',
    volume: 0.4,
    refineTo: { wakemetal: 60, gatesong: 8 },
    basePrice: 70,
  },
};

export const MINERALS = ['ferrite', 'lumen', 'wakemetal', 'gatesong'] as const;
